/**
 * Hook for fetching and caching FormSchema
 * 
 * Fetches the active form schema for a given form type, applicant type, and competition year.
 * Caches the result to avoid redundant API calls.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import type { FormSchema, FormField, ApplicantType, FormType } from '@/types';
import { getActiveFormSchema, listFormFields } from '@/graphql/queries';

const client = generateClient();

// Simple in-memory cache for form schemas
const schemaCache = new Map<string, { schema: FormSchema; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface UseFormSchemaOptions {
  formType: FormType;
  applicantType: ApplicantType;
  competitionYear: number;
  skip?: boolean;
}

interface UseFormSchemaResult {
  formSchema: FormSchema | null;
  fields: FormField[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface GetActiveFormSchemaResponse {
  getActiveFormSchema: FormSchema | null;
}

interface ListFormFieldsResponse {
  listFormFields: FormField[];
}

function getCacheKey(formType: FormType, applicantType: ApplicantType, competitionYear: number): string {
  return `${formType}-${applicantType}-${competitionYear}`;
}

export function useFormSchema({
  formType,
  applicantType,
  competitionYear,
  skip = false,
}: UseFormSchemaOptions): UseFormSchemaResult {
  const [formSchema, setFormSchema] = useState<FormSchema | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchSchema = useCallback(async () => {
    if (skip) return;

    // Check cache first
    const cacheKey = getCacheKey(formType, applicantType, competitionYear);
    const cached = schemaCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setFormSchema(cached.schema);
      setFields(cached.schema.fields || []);
      setLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Fetch the active form schema
      const schemaResponse = await client.graphql({
        query: getActiveFormSchema,
        variables: { formType, applicantType, competitionYear },
        authMode: 'userPool',
      }) as GraphQLResult<GetActiveFormSchemaResponse>;

      const schema = schemaResponse.data?.getActiveFormSchema ?? null;
      
      if (!schema) {
        setFormSchema(null);
        setFields([]);
        setLoading(false);
        return;
      }

      // Fetch the form fields for this schema
      const fieldsResponse = await client.graphql({
        query: listFormFields,
        variables: { formSchemaId: schema.id },
        authMode: 'userPool',
      }) as GraphQLResult<ListFormFieldsResponse>;

      const fetchedFields = fieldsResponse.data?.listFormFields || [];
      
      // Sort fields by displayOrder
      const sortedFields = [...fetchedFields].sort((a, b) => a.displayOrder - b.displayOrder);

      // Combine schema with fields
      const schemaWithFields: FormSchema = {
        ...schema,
        fields: sortedFields,
      };

      // Update cache
      schemaCache.set(cacheKey, { schema: schemaWithFields, timestamp: Date.now() });

      setFormSchema(schemaWithFields);
      setFields(sortedFields);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Ignore aborted requests
      }
      setError(err instanceof Error ? err : new Error('Failed to fetch form schema'));
    } finally {
      setLoading(false);
    }
  }, [formType, applicantType, competitionYear, skip]);

  const refetch = useCallback(() => {
    // Clear cache for this key to force refetch
    const cacheKey = getCacheKey(formType, applicantType, competitionYear);
    schemaCache.delete(cacheKey);
    fetchSchema();
  }, [formType, applicantType, competitionYear, fetchSchema]);

  useEffect(() => {
    fetchSchema();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchSchema]);

  return { formSchema, fields, loading, error, refetch };
}
